import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { Loader2, Plus, TriangleAlert, X } from "lucide-react";
import type { ManualAssignedUser } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SearchField } from "@/components/ui/search-field";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useManualAssignedUsers,
  useAssignableUsers,
  useAssignManualUser,
  useUnassignManualUser,
  type ManualMutationError,
} from "./useManualMutations.js";

export interface ManageReviewersDialogProps {
  /** The manual whose reviewers are being managed. `name` is for the dialog copy; `id` drives all the calls. */
  manual: { id: number; name: string };
  session: AuthSession | null;
  onClose: () => void;
}

/** Up to two initials from a display name (First Last → FL; else the first two letters). Mirrors the drawer's avatar. */
function initials(u: ManualAssignedUser): string {
  const fromParts = [u.firstName, u.lastName].filter(Boolean).map((s) => s![0]!.toUpperCase());
  if (fromParts.length > 0) return fromParts.join("").slice(0, 2);
  const base = (u.name || "?").replace(/[^\p{L}\p{N}]+/gu, "");
  return (base.slice(0, 2) || "?").toUpperCase();
}

function Avatar({ u }: { u: ManualAssignedUser }) {
  return (
    <span
      aria-hidden="true"
      className="bg-muted text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-medium"
    >
      {initials(u)}
    </span>
  );
}

/**
 * Manage-reviewers dialog (ADR-022 Phase 2, legacy `Anpassen`). Opened from the drawer's "Assigned users" section for a
 * facility-`confirm` caller. Two lists: the current reviewers (each removable via an ×) and a searchable set of candidate
 * reviewers (users with `manuals_manage` on the manual's facility) to add. Assign/unassign are `confirm`-gated + audited
 * server-side; on success both queries invalidate so the lists (and the drawer's read-only list) refresh. Token-only
 * styling (both themes). Self-contained (own queries + mutations), rendered by the manuals view like the delete dialog.
 */
export function ManageReviewersDialog({ manual, session, onClose }: ManageReviewersDialogProps) {
  const { t } = useTranslation();
  const assigned = useManualAssignedUsers(session, manual.id);
  const candidates = useAssignableUsers(session, manual.id, true);
  const assign = useAssignManualUser(session);
  const unassign = useUnassignManualUser(session);
  const [query, setQuery] = useState("");

  // Client-side name filter over the fetched candidates (legacy filtered the checkbox list the same way).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = candidates.data ?? [];
    return q ? list.filter((u) => u.name.toLowerCase().includes(q)) : list;
  }, [candidates.data, query]);

  const addingId = assign.isPending ? assign.variables?.userId : undefined;
  const removingId = unassign.isPending ? unassign.variables?.userId : undefined;
  const muterror = (assign.error ?? unassign.error) as ManualMutationError | undefined;

  const doAssign = (userId: number) => assign.mutate({ id: manual.id, userId });
  const doUnassign = (userId: number) => unassign.mutate({ id: manual.id, userId });

  const assignedList = assigned.data ?? [];

  return (
    <Dialog isOpen onOpenChange={(open) => !open && onClose()}>
      <DialogTitle>{t("manualManagement.reviewers.title")}</DialogTitle>
      <DialogDescription>
        {t("manualManagement.reviewers.assignTo")} <strong className="text-foreground">{manual.name}</strong>.
      </DialogDescription>

      {/* Current reviewers — each removable. */}
      <div className="flex flex-col gap-1.5">
        <p className="text-muted-foreground text-xs font-medium">{t("manualManagement.reviewers.assigned")}</p>
        {assigned.isPending ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("common.loading")}
          </p>
        ) : assigned.isError ? (
          <p className="text-muted-foreground text-sm">{t("manualManagement.details.loadReviewersFailed")}</p>
        ) : assignedList.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("manualManagement.reviewers.none")}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {assignedList.map((u) => (
              <li key={u.userId} className="border-border bg-background flex items-center gap-2 rounded-full border py-1 pr-1 pl-2 text-sm">
                <Avatar u={u} />
                <span className="max-w-[12rem] truncate">{u.name}</span>
                <TooltipTrigger delay={0}>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("manualManagement.reviewers.removePerson", { name: u.name })}
                    className="size-6 rounded-full"
                    onPress={() => doUnassign(u.userId)}
                    isDisabled={removingId === u.userId}
                  >
                    {removingId === u.userId ? (
                      <Loader2 aria-hidden="true" className="size-3.5 animate-spin" />
                    ) : (
                      <X aria-hidden="true" className="size-3.5" />
                    )}
                  </Button>
                  <Tooltip>{t("common.remove")}</Tooltip>
                </TooltipTrigger>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add — searchable candidate list. */}
      <div className="flex flex-col gap-1.5">
        {/* The visible heading; the field itself is named by `searchLabel` (its aria-label, as before). */}
        <span className="text-muted-foreground text-xs font-medium">{t("manualManagement.reviewers.addLabel")}</span>
        <SearchField
          label={t("manualManagement.reviewers.searchLabel")}
          placeholder={t("manualManagement.reviewers.searchPlaceholder")}
          value={query}
          onChange={setQuery}
          clearLabel={t("manualManagement.toolbar.clearSearch")}
        />

        <div className="border-border max-h-56 overflow-y-auto rounded-md border">
          {candidates.isPending ? (
            <p className="text-muted-foreground flex items-center gap-2 p-3 text-sm">
              <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("manualManagement.reviewers.loadingCandidates")}
            </p>
          ) : candidates.isError ? (
            <p className="text-muted-foreground p-3 text-sm">{t("manualManagement.reviewers.loadCandidatesFailed")}</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground p-3 text-sm">
              {query.trim() ? t("manualManagement.reviewers.noMatches") : t("manualManagement.reviewers.noneLeft")}
            </p>
          ) : (
            <ul className="divide-border divide-y">
              {filtered.map((u) => (
                <li key={u.userId} className="flex items-center gap-2 p-2">
                  <Avatar u={u} />
                  <span className="min-w-0 flex-1 truncate text-sm">{u.name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => doAssign(u.userId)}
                    isDisabled={addingId === u.userId}
                  >
                    {addingId === u.userId ? (
                      <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                    ) : (
                      <Plus aria-hidden="true" className="size-4" />
                    )}
                    {t("common.add")}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {muterror ? (
        <div role="alert" className="text-destructive flex items-center gap-1.5 text-sm">
          <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
          {muterror.code === "MANUAL_ASSIGNEE_INELIGIBLE"
            ? t("manualManagement.reviewers.personGone")
            : mutError(muterror, t)}
        </div>
      ) : null}

      <DialogFooter>
        <Button variant="ghost" onPress={onClose}>
          {t("common.done")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function mutError(err: ManualMutationError, t: TFunction): string {
  if (err.code === "MANUAL_FORBIDDEN") return t("manualManagement.reviewers.forbidden");
  if (err.code === "MANUAL_NOT_FOUND") return t("manualManagement.reviewers.manualGone");
  return t("common.genericError");
}
